// RecordListTable.tsx
import { Profiler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import SysTable from "app/class/sys-table";
import SysColumn from "app/class/sys-column";
import { useRecordListContext } from "../hooks/useRecordListContext";
import { ColumnPreference } from "../hooks/useColumnPreferences";
import { useRecordListColumns } from "../hooks/useRecordListColumns";
import { RecordManagement } from "app/class/record-management";
import { useChoiceLabelMaps } from "modules/record/shared/hooks/useChoiceOptions";
import { useColumns } from "app/hooks/useColumns";
import { useTables } from "app/hooks/useTables";
import { resolveConditionColumns } from "../services/conditionSummary";

import RecordListNoTableAccessComponent from "./RecordListNoTableAccess";
import RecordListSkeletonComponent from "./record-list-views/RecordListSkeletonComponent";
import { Box } from "@mui/material";
import { useRecordListSort } from "../hooks/useRecordListSort";
import RecordModuleErrorState from "modules/record/shared/components/RecordModuleErrorState";
import { useTranslation } from "react-i18next";
import { emitRecordMutation } from "shared/services/recordMutationBus";
import { appendReturnToParam } from "modules/record/shared/services/recordNavigationReturn";
import { navigateFromClick } from "shared/services/navigationClick";
import RecordListEmptyState from "./RecordListEmptyState";

interface RecordListTableProps {
    currentTable: SysTable;
    tableColumns: SysColumn[];
    queryKey: any;
    actions?(params: any, table: SysTable): JSX.Element[];
    readOnly?: boolean;
    columnLabels?: Record<string, string>;
    isRelatedList: boolean;
    sortOptions: ReturnType<typeof useRecordListSort>;
    columnPreferences: ColumnPreference[];
    columnPreferencesLoading: boolean;
    hasActiveFilter?: boolean;
    onClearFilter?: () => void;
}

const GRID_DEBUG_STORAGE_KEY = "debug.recordListGrid";

const safeStringify = (value: unknown) => {
    try {
        return JSON.stringify(value);
    } catch (error) {
        return `[unserializable:${(error as Error).message}]`;
    }
};

const isGridDebugEnabled = () => {
    if (typeof window === "undefined") {
        return false;
    }

    return window.localStorage.getItem(GRID_DEBUG_STORAGE_KEY) === "1";
};

const buildDebugSnapshot = ({
    currentTable,
    page,
    pageSize,
    rows,
    rowCount,
    tableColumns,
    preferences,
    sortModel,
    queryParams,
    gridColumns,
}: {
    currentTable: SysTable;
    page: number;
    pageSize: number;
    rows: any[];
    rowCount: number;
    tableColumns: SysColumn[];
    preferences: ColumnPreference[];
    sortModel: ReturnType<typeof useRecordListSort>["sortModel"];
    queryParams: unknown;
    gridColumns: any[];
}) => ({
    table: currentTable.name,
    page,
    pageSize,
    rowsLength: rows.length,
    rowCount,
    tableColumnsLength: tableColumns.length,
    visiblePreferenceNames: preferences.filter((pref) => pref.visible).map((pref) => pref.name),
    sortModel,
    queryParams,
    gridColumns: gridColumns.map((column) => ({
        field: column.field,
        width: column.width,
        flex: column.flex,
        editable: column.editable,
        type: column.type,
    })),
});

const buildDiagnosticsFingerprint = ({
    currentTable,
    queryKey,
    queryParams,
    page,
    pageSize,
    rowCount,
    rows,
    tableColumns,
    preferences,
    sortPreference,
    sortModel,
    gridColumns,
    isRelatedList,
}: {
    currentTable: SysTable;
    queryKey: unknown;
    queryParams: unknown;
    page: number;
    pageSize: number;
    rowCount: number;
    rows: any[];
    tableColumns: SysColumn[];
    preferences: ColumnPreference[];
    sortPreference: ReturnType<typeof useRecordListSort>["sortPreference"];
    sortModel: ReturnType<typeof useRecordListSort>["sortModel"];
    gridColumns: any[];
    isRelatedList: boolean;
}) => ({
    table: currentTable.name,
    isRelatedList,
    page,
    pageSize,
    rowCount,
    rowIds: rows.map((row) => row?.id ?? null),
    tableColumnIds: tableColumns.map((column) => column.id),
    preferenceSignature: preferences.map((preference) => ({
        name: preference.name,
        visible: preference.visible,
        order: preference.order,
        width: preference.width ?? null,
    })),
    sortPreference,
    sortModel,
    queryParams,
    queryKey,
    gridColumnFields: gridColumns.map((column) => column.field),
});

const diffFingerprints = (
    previousFingerprint: Record<string, any> | null,
    nextFingerprint: Record<string, any>
) => {
    if (!previousFingerprint) {
        return Object.keys(nextFingerprint).reduce<Record<string, { before: unknown; after: unknown }>>(
            (accumulator, key) => {
                accumulator[key] = {
                    before: undefined,
                    after: nextFingerprint[key],
                };
                return accumulator;
            },
            {}
        );
    }

    return Object.keys(nextFingerprint).reduce<Record<string, { before: unknown; after: unknown }>>(
        (accumulator, key) => {
            const before = previousFingerprint[key];
            const after = nextFingerprint[key];

            if (JSON.stringify(before) !== JSON.stringify(after)) {
                accumulator[key] = { before, after };
            }

            return accumulator;
        },
        {}
    );
};

const useRecordListGridDiagnostics = ({
    currentTable,
    queryKey,
    page,
    pageSize,
    rows,
    rowCount,
    tableColumns,
    preferences,
    sortPreference,
    sortModel,
    queryParams,
    gridColumns,
    isRelatedList,
}: {
    currentTable: SysTable;
    queryKey: unknown;
    page: number;
    pageSize: number;
    rows: any[];
    rowCount: number;
    tableColumns: SysColumn[];
    preferences: ColumnPreference[];
    sortPreference: ReturnType<typeof useRecordListSort>["sortPreference"];
    sortModel: ReturnType<typeof useRecordListSort>["sortModel"];
    queryParams: unknown;
    gridColumns: any[];
    isRelatedList: boolean;
}) => {
    const debugEnabled = isGridDebugEnabled();
    const renderTimesRef = useRef<number[]>([]);
    const previousSnapshotRef = useRef<string | null>(null);
    const previousFingerprintRef = useRef<Record<string, any> | null>(null);

    useEffect(() => {
        if (!debugEnabled) {
            return;
        }

        const now = Date.now();
        const nextRenderTimes = [...renderTimesRef.current, now].filter(
            (timestamp) => now - timestamp < 2000
        );
        renderTimesRef.current = nextRenderTimes;

        if (nextRenderTimes.length >= 15) {
            console.warn("[RecordListGridDebug] High render frequency detected", {
                table: currentTable.name,
                rendersInLast2s: nextRenderTimes.length,
            });
            console.warn(`[RecordListGridDebugJSON] high-frequency ${safeStringify({
                table: currentTable.name,
                rendersInLast2s: nextRenderTimes.length,
                page,
                pageSize,
                rowCount,
                isRelatedList,
            })}`);
        }
    });

    useEffect(() => {
        if (!debugEnabled) {
            return;
        }

        const snapshot = buildDebugSnapshot({
            currentTable,
            page,
            pageSize,
            rows,
            rowCount,
            tableColumns,
            preferences,
            sortModel,
            queryParams,
            gridColumns,
        });
        const serializedSnapshot = JSON.stringify(snapshot);

        if (previousSnapshotRef.current !== serializedSnapshot) {
            console.groupCollapsed(`[RecordListGridDebug] Snapshot changed for ${currentTable.name}`);
            console.log(snapshot);
            console.trace();
            console.groupEnd();
            console.log(`[RecordListGridDebugJSON] snapshot ${currentTable.name} ${serializedSnapshot}`);
            previousSnapshotRef.current = serializedSnapshot;
        }
    }, [
        currentTable,
        debugEnabled,
        gridColumns,
        page,
        pageSize,
        preferences,
        queryParams,
        rowCount,
        rows,
        sortModel,
        tableColumns,
    ]);

    useEffect(() => {
        if (!debugEnabled) {
            return;
        }

        const fingerprint = buildDiagnosticsFingerprint({
            currentTable,
            queryKey,
            queryParams,
            page,
            pageSize,
            rowCount,
            rows,
            tableColumns,
            preferences,
            sortPreference,
            sortModel,
            gridColumns,
            isRelatedList,
        });
        const changedInputs = diffFingerprints(previousFingerprintRef.current, fingerprint);
        const changedKeys = Object.keys(changedInputs);

        if (changedKeys.length > 0) {
            console.groupCollapsed(`[RecordListGridDebug] Render inputs changed for ${currentTable.name}`);
            console.log({
                changedKeys,
                changedInputs,
                isRelatedList,
            });
            console.groupEnd();
            console.log(`[RecordListGridDebugJSON] inputs ${currentTable.name} ${safeStringify({
                changedKeys,
                changedInputs,
                isRelatedList,
            })}`);
            previousFingerprintRef.current = fingerprint;
        }
    }, [
        currentTable,
        debugEnabled,
        gridColumns,
        isRelatedList,
        page,
        pageSize,
        preferences,
        queryKey,
        queryParams,
        rowCount,
        rows,
        sortModel,
        sortPreference,
        tableColumns,
    ]);

    return debugEnabled;
};

const RecordListTableComponent = ({
    currentTable,
    tableColumns,
    queryKey,
    actions,
    readOnly = false,
    columnLabels,
    isRelatedList,
    columnPreferences,
    columnPreferencesLoading,
    hasActiveFilter = false,
    onClearFilter,
    sortOptions: { sortPreference, sortModel, sortLoading, handleSortModelChange }
}: RecordListTableProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const recordListContext = useRecordListContext();
    const { setSelection, clearSelection, queryParams, setPermissions, setReadDenied } = recordListContext;
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);

    const preferences = columnPreferences;
    const prefsLoading = columnPreferencesLoading;
    const { data: allColumns = [] } = useColumns();
    const { data: tables = [] } = useTables();

    // const { sortPreference, sortModel, handleSortModelChange } = useRecordListSort(currentTable.name);
    const { data: response, isLoading, isError, error } = useQuery({
        queryKey: [...queryKey, page + 1, pageSize],
        queryFn: async () => {
            const rm = new RecordManagement(currentTable.name).referenceMode("display");
            if (queryParams) rm.builderFromQuery(queryParams);
            if (sortPreference) {
                sortPreference.forEach(({ field, sort }) => {
                    rm.orderBy(field, sort);
                });
            }
            rm.paginate(page + 1, pageSize);
            return rm.run();
        },
        enabled: !!currentTable && sortLoading === false,
        placeholderData: (previousData, previousQuery) => (previousQuery?.queryKey[1] === currentTable.name ? previousData : undefined),
        retry: (failureCount, error) => (error as any)?.response?.status !== 403 && failureCount < 3,
    });

    const rows = useMemo(() => response?.data ?? [], [response?.data]);
    const conditionChoiceColumns = useMemo(() => {
        const targetIds = new Set<string>();
        tableColumns.filter((column) => column.column_type === "CONDITION").forEach((column) => {
            rows.forEach((row: any) => {
                resolveConditionColumns(column, row, tableColumns, allColumns, tables).forEach((targetColumn) => {
                    if (targetColumn.column_type === "CHOICE") targetIds.add(targetColumn.id);
                });
            });
        });
        return allColumns.filter((column) => targetIds.has(column.id));
    }, [allColumns, rows, tableColumns, tables]);
    const choiceSourceColumns = useMemo(() => {
        const concreteIds = new Set(rows.map((row: any) => tables.find(table => table.name === row?.sys_class_name)?.id).filter(Boolean));
        const concreteColumns = tableColumns.filter(column => column.column_type === "CHOICE").flatMap(column =>
            Array.from(concreteIds).filter(id => id !== column.sys_table_id).map(id =>
                Object.assign(Object.create(Object.getPrototypeOf(column)), column, {
                    sys_table_id: id,
                    source_sys_table_id: column.source_sys_table_id || column.sys_table_id,
                }) as SysColumn));
        return [...tableColumns, ...conditionChoiceColumns, ...concreteColumns];
    }, [tableColumns, conditionChoiceColumns, rows, tables]);
    const choiceLabelMaps = useChoiceLabelMaps(choiceSourceColumns);
    useEffect(() => {
        setPermissions(response?.meta?.permissions);
        setReadDenied(Boolean(isError && ((error as any)?.response?.status === 403 || (error as any)?.response?.data?.error?.code === "FORBIDDEN")));
    }, [response?.meta?.permissions, isError, error, setPermissions, setReadDenied]);
    const rowCount = response?.meta?.total ?? 0;

    // Escopo `field` de ACL não suporta condições por registro, então o
    // conjunto de campos "hidden" é uniforme entre as linhas de uma mesma
    // resposta — basta varrer todas para montar o conjunto.
    const hiddenFieldNames = useMemo(() => {
        const hidden = new Set<string>();
        rows.forEach((row: any) => {
            const permissions = row?._field_permissions as Record<string, string> | undefined;
            if (!permissions) return;
            Object.entries(permissions).forEach(([field, state]) => {
                if (state === "hidden") hidden.add(field);
            });
        });
        return hidden;
    }, [rows]);

    useEffect(() => {
        setPage(0);
        clearSelection();
    }, [clearSelection, currentTable.id, queryParams, sortPreference]);

    const handleNavigate = useCallback((id: string, event?: React.MouseEvent<HTMLElement>) => {
        const url = `/home/workspace/${currentTable.name}/record/${id}`;
        navigateFromClick(event, navigate, appendReturnToParam(url, `${location.pathname}${location.search}${location.hash}`));
    }, [currentTable.name, location.hash, location.pathname, location.search, navigate]);

    const gridColumns = useRecordListColumns({
        currentTable,
        tableColumns,
        preferences,
        choiceLabelMaps,
        allColumns,
        tables,
        actions,
        readOnly,
        columnLabels,
        isRelatedList,
        onNavigate: handleNavigate,
        hiddenFieldNames,
    });
    const gridFields = useMemo(() => gridColumns.map((column) => column.field), [gridColumns]);
    const gridSortModel = useMemo(
        () => sortModel.filter((item) => gridColumns.some((column) => column.field === item.field)),
        [sortModel, gridColumns]
    );
    const gridDebugEnabled = useRecordListGridDiagnostics({
        currentTable,
        queryKey,
        page,
        pageSize,
        rows,
        rowCount,
        tableColumns,
        preferences,
        sortPreference,
        sortModel,
        queryParams,
        gridColumns,
        isRelatedList,
    });
    const dataGridLocaleText = useMemo(() => ({
        noRowsLabel: t("No rows"),
        noResultsOverlayLabel: t("No results found"),
        errorOverlayDefaultLabel: t("An error occurred."),
        toolbarDensity: t("Density"),
        toolbarDensityLabel: t("Density"),
        toolbarDensityCompact: t("Compact"),
        toolbarDensityStandard: t("Standard"),
        toolbarDensityComfortable: t("Comfortable"),
        toolbarColumns: t("Columns"),
        toolbarColumnsLabel: t("Select columns"),
        toolbarFilters: t("Filters"),
        toolbarFiltersLabel: t("Show filters"),
        toolbarFiltersTooltipHide: t("Hide filters"),
        toolbarFiltersTooltipShow: t("Show filters"),
        toolbarFiltersTooltipActive: (count: number) => t("Active filters", { count }),
        toolbarExport: t("Export"),
        toolbarExportLabel: t("Export"),
        toolbarExportCSV: t("Download as CSV"),
        toolbarExportPrint: t("Print"),
        columnsPanelTextFieldLabel: t("Find column"),
        columnsPanelTextFieldPlaceholder: t("Column title"),
        columnsPanelDragIconLabel: t("Reorder column"),
        columnsPanelShowAllButton: t("Show all"),
        columnsPanelHideAllButton: t("Hide all"),
        filterPanelAddFilter: t("Add filter"),
        filterPanelDeleteIconLabel: t("Delete"),
        filterPanelLinkOperator: t("Logic operator"),
        filterPanelOperators: t("Operator"),
        filterPanelOperatorAnd: t("And"),
        filterPanelOperatorOr: t("Or"),
        filterPanelColumns: t("Columns"),
        filterPanelInputLabel: t("Value"),
        filterPanelInputPlaceholder: t("Filter value"),
        filterOperatorContains: t("contains"),
        filterOperatorEquals: t("equals"),
        filterOperatorStartsWith: t("starts with"),
        filterOperatorEndsWith: t("ends with"),
        filterOperatorIs: t("is"),
        filterOperatorNot: t("is not"),
        filterOperatorAfter: t("after"),
        filterOperatorOnOrAfter: t("on or after"),
        filterOperatorBefore: t("before"),
        filterOperatorOnOrBefore: t("on or before"),
        filterOperatorIsEmpty: t("is empty"),
        filterOperatorIsNotEmpty: t("is not empty"),
        filterOperatorIsAnyOf: t("is one of"),
        filterValueAny: t("Any"),
        filterValueTrue: t("True"),
        filterValueFalse: t("False"),
        columnMenuLabel: t("Menu"),
        columnMenuShowColumns: t("Show columns"),
        columnMenuFilter: t("Filter"),
        columnMenuHideColumn: t("Hide column"),
        columnMenuUnsort: t("Unsort"),
        columnMenuSortAsc: t("Sort ascending"),
        columnMenuSortDesc: t("Sort descending"),
        columnHeaderFiltersTooltipActive: (count: number) => t("Active filters", { count }),
        columnHeaderFiltersLabel: t("Show filters"),
        columnHeaderSortIconLabel: t("Sort"),
        footerRowSelected: (count: number) => t("Rows selected", { count }),
        footerTotalRows: t("Total rows"),
        footerTotalVisibleRows: (visibleCount: number, totalCount: number) =>
            `${visibleCount.toLocaleString()} ${t("of")} ${totalCount.toLocaleString()}`,
        checkboxSelectionHeaderName: t("Checkbox selection"),
        booleanCellTrueLabel: t("True"),
        booleanCellFalseLabel: t("False"),
        actionsCellMore: t("More"),
        MuiTablePagination: {
            labelRowsPerPage: t("Rows per page"),
            labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
                `${from}-${to} ${t("of")} ${count !== -1 ? count : `${t("more than")} ${to}`}`,
            getItemAriaLabel: (type: string) => {
                if (type === "first") return t("Go to first page");
                if (type === "last") return t("Go to last page");
                if (type === "next") return t("Go to next page");
                return t("Go to previous page");
            },
        },
    }), [t]);

    const isCellEditable = useCallback((params: any) => {
        const permission = params.row?._field_permissions?.[params.field];
        return !readOnly && permission !== "readonly" && permission !== "hidden";
    }, [readOnly]);

    const handleProcessRowUpdate = useCallback(async (newRow: any, oldRow: any) => {
        const rm = new RecordManagement(currentTable.name);
        const fieldPermissions = oldRow?._field_permissions as Record<string, string> | undefined;
        const changedFields = Object.keys(newRow).reduce<Record<string, any>>((acc, key) => {
            if (key === "id" || key === "_field_permissions") {
                return acc;
            }

            const permission = fieldPermissions?.[key];
            if (permission === "readonly" || permission === "hidden") {
                return acc;
            }

            if (newRow[key] !== oldRow[key]) {
                acc[key] = newRow[key];
            }

            return acc;
        }, {});

        if (Object.keys(changedFields).length === 0) {
            return newRow;
        }

        await rm.update(newRow.id, changedFields);
        emitRecordMutation({
            table: currentTable.name,
            operation: "update",
            ids: [newRow.id],
            source: "record_list_inline_edit",
        });
        return newRow;
    }, [currentTable.name]);

    if (isLoading || prefsLoading || sortLoading) {
        return <RecordListSkeletonComponent columnsCount={gridColumns.length} rowsCount={20} />;
    }

    if (isError && (error as any).response?.data?.error?.code === "FORBIDDEN") {
        return <RecordListNoTableAccessComponent />;
    }

    if (isError) {
        const isUnprocessableEntity = (error as any)?.response?.status === 422;
        return (
            <RecordModuleErrorState
                title={String(t(isUnprocessableEntity ? "record.list.filter.apply_failed" : "We couldn't load the records"))}
                description={String(t(isUnprocessableEntity ? "record.list.filter.rejected" : "This list is temporarily unavailable. You can try again without leaving the page."))}
                error={error}
                onRetry={() => queryClient.invalidateQueries({ queryKey })}
            />
        );
    }

    const dataGrid = (
        <DataGrid
            rows={rows}
            columns={gridColumns}
            sx={{
                border: 0,
                borderRadius: 0,
                ...(!isRelatedList && {
                    overflow: "visible",
                    "& .MuiDataGrid-main": { overflow: "visible" },
                    "& .MuiDataGrid-columnHeaders": {
                        position: "sticky",
                        top: "var(--record-list-toolbar-height, 49px)",
                        zIndex: 2,
                        backgroundColor: "var(--prm-surface)",
                    },
                }),
                "& .MuiDataGrid-cell--editing": {
                    overflow: "visible",
                    zIndex: 2,
                },
                "& .MuiDataGrid-row": {
                    zIndex: "auto",
                },
                "& .MuiDataGrid-virtualScroller": {
                    // DataGrid sets an inline margin-top equal to the header height because the header is
                    // normally absolute; the List's sticky header is in flow, so that margin is a blank gap.
                    ...(!isRelatedList && { marginTop: "0 !important" }),
                    scrollbarColor: (theme) => `${theme.palette.grey[theme.palette.mode === "dark" ? 600 : 500]} ${theme.palette.grey[theme.palette.mode === "dark" ? 900 : 100]}`,
                    scrollbarWidth: "thin",
                },
                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": { width: 10, height: 10 },
                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                    backgroundColor: (theme) => theme.palette.grey[theme.palette.mode === "dark" ? 900 : 100],
                },
                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                    backgroundColor: (theme) => theme.palette.grey[theme.palette.mode === "dark" ? 600 : 500],
                    border: (theme) => `2px solid ${theme.palette.grey[theme.palette.mode === "dark" ? 900 : 100]}`,
                    borderRadius: 8,
                },
                "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-button": { display: "none", width: 0, height: 0 },

            }}
            pagination
            paginationMode="server"
            page={page}
            onPageChange={(nextPage) => {
                setPage(nextPage);
                clearSelection();
            }}
            pageSize={pageSize}
            onPageSizeChange={(nextPageSize) => {
                setPageSize(nextPageSize);
                setPage(0);
                clearSelection();
            }}
            rowsPerPageOptions={[20, 50, 100]}
            rowCount={rowCount}
            autoHeight
            checkboxSelection
            disableSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            editMode="cell"
            experimentalFeatures={{ newEditingApi: true }}
            isCellEditable={isCellEditable}
            processRowUpdate={handleProcessRowUpdate}
            onProcessRowUpdateError={(error) => {
                console.error("[RecordListGridDebug] Row update failed", {
                    table: currentTable.name,
                    page,
                    pageSize,
                    sortModel,
                    error,
                });
            }}
            onSelectionModelChange={(ids) => {
                const selected = rows?.filter(r => ids.includes(r.id)) || [];
                setSelection(ids as string[], selected);
            }}
            sortModel={gridSortModel}
            onSortModelChange={(model) => handleSortModelChange(model, gridFields)}
            sortingMode="server"
            localeText={dataGridLocaleText as any}
            components={!isRelatedList ? { NoRowsOverlay: () => <RecordListEmptyState filtered={hasActiveFilter} onClear={onClearFilter || (() => {})} /> } : undefined}
        />
    );

    return (
        <Box>
            {gridDebugEnabled ? (
                <Profiler
                    id={`RecordListTable:${currentTable.name}`}
                    onRender={(
                        id,
                        phase,
                        actualDuration,
                        baseDuration,
                    ) => {
                        console.log("[RecordListGridDebug] Profiler", {
                            id,
                            phase,
                            actualDuration,
                            baseDuration,
                            table: currentTable.name,
                            page,
                            pageSize,
                        });
                    }}
                >
                    {dataGrid}
                </Profiler>
            ) : dataGrid}
        </Box>
    );
};

export default RecordListTableComponent;
